/**
 * EpisodeAdapter Service
 * 
 * RESPONSIBILITY:
 * - Accept raw Series Data (Firestore Doc) and Subcollection content
 * - Normalize EVERYTHING into a standardized 'Episode' format
 * - Handle Type-specific mapping rules ISOLATED from the UI
 * - Enforce ID consistency (String)
 * 
 * "Safety Buffer" between DB and UI.
 */
export const EpisodeAdapter = {
    /**
     * Adapt raw data to Unified Episode List
     * @param {Object} seriesData - The main series document data
     * @param {Array} subcollectionEpisodes - (Optional) Raw episodes from 'episodes' subcollection (for DaVinci)
     * @returns {Array} unifiedEpisodes - Standardized array of Episode objects
     */
    adapt: (seriesData, subcollectionEpisodes = []) => {
        if (!seriesData) return [];

        let unified = [];
        const coverImage = seriesData.coverImage || seriesData.thumbnail || '';

        // STRATEGY: Check Data Existence Priority (Robust Loading)

        // STRATEGY: Check Data Existence Priority (Robust Loading)

        // 1. Subcollection Logic (Priority: Subcollection existence - NEW ARCHITECTURE)
        // [Priority Correction] Always check subcollection first.
        if (subcollectionEpisodes && subcollectionEpisodes.length > 0) {
            // [FIX] Deterministic Sorting v1.0 (Order -> CreatedAt -> Title)
            unified = subcollectionEpisodes
                .sort((a, b) => {
                    // 1. Primary: Explicit Order (Higher order comes later usually, verifying user intent)
                    // Codebase assumes 'order' is ascending (1, 2, 3...)
                    const orderDiff = (a.order || 0) - (b.order || 0);
                    if (orderDiff !== 0) return orderDiff;

                    // 2. Secondary: Creation Date (Earlier created comes first)
                    const timeA = a.createdAt ? (a.createdAt.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime()) : 0;
                    const timeB = b.createdAt ? (b.createdAt.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime()) : 0;
                    const timeDiff = timeA - timeB; // Ascending
                    if (timeDiff !== 0) return timeDiff;

                    // 3. Tertiary: Title (Natural Sort) - Final Tie Breaker
                    // Fixes "Ep 1, Ep 10, Ep 2" sorting issue.
                    const titleA = (a.title || "").trim();
                    const titleB = (b.title || "").trim();

                    return titleA.localeCompare(titleB, undefined, { numeric: true, sensitivity: 'base' });

                    return 0; // Truly identical
                })
                .map((ep, idx) => ({
                    id: String(ep.id), // UUID String
                    originalId: ep.id || null, // [FIX] Prevent undefined
                    title: ep.title || `Episode ${idx + 1}`, // Fallback title also syncs
                    thumbnail: ep.thumbnail || ep.formattedThumbnail || coverImage,
                    thumb: ep.thumbnail || ep.formattedThumbnail || ep.thumb || coverImage, // [FIX] Legacy Schema

                    // [FIX] Webtoon Image Data Passthrough
                    imageUrls: ep.imageUrls || ep.links || [],
                    links: ep.links || ep.imageUrls || [],     // Compatibility

                    index: idx + 1, // [FIX] Force Sequential Indexing (Ignore gaps in ep.order)
                    status: ep.status || 'published', // [Safety] Legacy DaVinci might miss status
                    type: seriesData.type || 'davinci', // Inherit from Series if possible
                    bgmUrl: ep.bgmUrl || ep.bgm || null, // [FIX] Restore BGM
                    createdAt: ep.createdAt || ep.updatedAt,
                    blocks: ep.blocks || [], // [Enable Editor Support]
                    content: ep.content || [], // [Hybrid Support]
                    displayMode: ep.displayMode || 'webtoon' // [FIX] Persist Display Mode
                }));
        }

        // 2. [MIGRATION] Legacy Fallbacks REMOVED
        // As of v1.36.195, we strictly prioritize Subcollections.
        // If subcollectionEpisodes is empty, we return empty list to encourage migration/sync.

        else if (seriesData.episodes && Array.isArray(seriesData.episodes) && seriesData.episodes.length > 0) {
            // [RESTORED] Webtoon Legacy Logic (Array Support)
            // Necessary because WebtoonEditor still saves to this array.
            unified = seriesData.episodes.map((ep, idx) => ({
                id: String(idx), // Array index as ID
                originalId: null,
                title: ep.title || `Episode ${idx + 1}`,
                thumbnail: ep.thumb || coverImage,
                thumb: ep.thumb || coverImage,
                imageUrls: ep.links || [],
                links: ep.links || [],
                index: idx + 1,
                status: ep.status || 'published', // Assume published if missing
                type: 'webtoon',
                bgmUrl: ep.bgmUrl || null,
                createdAt: ep.updatedAt || new Date(),
                blocks: [], // Webtoon has no blocks
                content: [],
                displayMode: ep.displayMode || 'webtoon' // [FIX] Persist Display Mode
            }));
        }

        // 4. Final Sanity Check & Sort
        // Ensure index order is correct
        return unified.sort((a, b) => a.index - b.index);
    },

    /**
     * Filter for Readers (Hide Drafts)
     * @param {Array} episodes - Unified Episode List
     * @param {Boolean} isAuthor - If true, show everything
     * @returns {Array}
     */
    filterForReader: (episodes, isAuthor = false) => {
        if (isAuthor) return episodes; // Authors see all

        return episodes.filter(ep => {
            // "Ghost Logic Prevention":
            // If status is explicit 'draft', hide it.
            // If status is missing/undefined, assume 'legacy/published' and SHOW it.
            return ep.status !== 'draft';
        });
    }
};
